import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { RouteComponentProps } from 'react-router-dom';

import { Routes } from '../../../classes/Routes';
import axios from '../../../axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCopy, faArrowLeft, faMapMarkerAlt, faExternalLinkAlt, faLock, faEdit, faTimes } from '@fortawesome/free-solid-svg-icons';
import OfferRequestsInfo from '../../../components/OfferRequests/OfferRequestsInfo/OfferRequestsInfo';
import OfferEdit from '../../../components/Offers/OfferEdit/OfferEdit';
import AttachmentsList from './AttachmentsList';
import Alert, { EAlertColor } from '../../../components/UI/Alert/Alert';
import Button from '../../../components/UI/Button/Button';
import ConfirmDialog from '../../../components/UI/ConfirmDialog/ConfirmDialog';
import Container from '../../../components/UI/Container/Container';
import { EInputType, IInputField } from '../../../components/UI/Input/Input';
import { useInputs } from '../../../components/UI/Input/useInputs';
import InputGroup from '../../../components/UI/InputGroup/InputGroup';
import ModalContext, { defaultModal, EModalSize } from '../../../components/UI/Modal/ModalContext';
import Spinner from '../../../components/UI/Spinner/Spinner';
import { EInputUpdateAction } from '../../../context/InputContext';
import { buildingTypes, languages } from '../../../data/select-data';
import { EBuildingType, EOfferRequestType } from '../../../enums';
import { useAuthUser } from '../../../hooks/useAuthUser';
import { useOrganizationOptions } from '../../../hooks/useOrganizationOptions';
import { useServiceOptions } from '../../../hooks/useServiceOptions';
import { IAppState, IOfferRequest, IOfferRequestState, IService, IServiceState } from '../../../interfaces';
import { formatDateString, formatDateTimeString, getInputData, initForm, parseDate } from '../../../shared/utility';
import * as actions from '../../../store/actions';
import { ETranslation } from '../../../translations/translation-keys';
import { getDynamicFieldsForService } from '../../../config/forms';

interface IMatch {
  id: string;
}

interface IProps extends RouteComponentProps<IMatch> { }

export enum EOfferRequest {
  operator = "operator",
  firstName = "firstName",
  lastName = "lastName",
  address = "address",
  phone = "phone",
  email = "email",
  service = "service",
  archived = "archived",
  area = "area",
  buildingType = "buildingType",
  buildingTypeOther = "buildingTypeOther",
  constructionYear = "constructionYear",
  zip = "zip",
  city = "city",
  additionalInfo = "additionalInfo",
  province = "province",
  municipality = "municipality",
  language = "language",
  length = "length",
  regular = "regular"
}

const mapBuildingTypeToEnum = (value: string): string => {
  if (!value) return "";
  const lowerVal = value.toLowerCase().trim();
  if (lowerVal === "kerrostalo" || lowerVal === "asuinkerrostalo" || lowerVal === "apartmenthouse" || lowerVal === "apartment") {
    return "apartmentHouse";
  }
  if (lowerVal === "omakotitalo" || lowerVal === "detachedhouse" || lowerVal === "detached" || lowerVal === "hirsitalo") {
    return "detachedHouse";
  }
  if (lowerVal === "rivitalo" || lowerVal === "rowhouse" || lowerVal === "row") {
    return "rowHouse";
  }
  if (lowerVal === "paritalo" || lowerVal === "semidetachedhouse" || lowerVal === "semidetached" || lowerVal === "ketjutalo") {
    return "semidetachedHouse";
  }
  if (lowerVal === "kaupunkipientalo" || lowerVal === "townhouse" || lowerVal === "town_house") {
    return "townHouse";
  }
  if (lowerVal === "muu" || lowerVal === "other" || lowerVal === "muu rakennus") {
    return "other";
  }
  return value;
};

const formatRelativeTime = (dateStr?: string | null): { formatted: string, relative: string } | null => {
  if (!dateStr) return null;
  const date = parseDate(dateStr);
  if (!date) return null;

  const pad = (num: number) => num.toString().padStart(2, '0');
  const d = pad(date.getDate());
  const m = pad(date.getMonth() + 1);
  const y = date.getFullYear();
  const h = pad(date.getHours());
  const min = pad(date.getMinutes());
  const formatted = `${d}.${m}.${y} klo ${h}:${min}`;

  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  let relative = '';
  if (diffMs < 0) {
    relative = 'tulevaisuudessa';
  } else if (diffMins < 1) {
    relative = 'aivan äsken';
  } else if (diffMins < 60) {
    relative = `${diffMins} min sitten`;
  } else if (diffHours < 24) {
    relative = `${diffHours} h sitten`;
  } else {
    relative = `${diffDays} pv sitten`;
  }

  return { formatted, relative };
};

const OfferRequest: React.FC<IProps> = ({ match, history }) => {
  const dispatch = useDispatch();
  const { setModal } = useContext(ModalContext);
  const { organizationOptions, organizationsLoading } = useOrganizationOptions({
    withBusinessId: true,
    withPlaceholder: true,
  });
  const [service, setService] = useState<IService | null>(null);

  const authUser = useAuthUser();
  const { loading, error, offerRequest, saveOrUpdateOk } = useSelector<IAppState, IOfferRequestState>(state => state.offerRequest);
  const { services } = useSelector<IAppState, IServiceState>(state => state.services);

  const { t } = useTranslation();
  const { id } = match.params;
  const [isEditing, setIsEditing] = useState<boolean>(Routes.isNew(id));

  const [inputs, setInputs] = useState<IInputField>({
    [EOfferRequest.operator]: {
      type: EInputType.reactSelect,
      labelTranslation: ETranslation.COMMON_OPERATOR_NAME,
      placeholderTranslation: ETranslation.COMMON_OPERATOR_NAME,
      value: "",
      options: [],
      validation: {
        required: authUser?.isPrimary,
      },
    },
    [EOfferRequest.firstName]: {
      type: EInputType.text,
      labelTranslation: ETranslation.OFFERREQUEST_FIRSTNAME,
      placeholderTranslation: ETranslation.OFFERREQUEST_FIRSTNAME,
      value: "",
    },
    [EOfferRequest.lastName]: {
      type: EInputType.text,
      labelTranslation: ETranslation.OFFERREQUEST_LASTNAME,
      placeholderTranslation: ETranslation.OFFERREQUEST_LASTNAME,
      value: "",
    },
    [EOfferRequest.address]: {
      type: EInputType.text,
      labelTranslation: ETranslation.COMMON_STREET_ADDRESS,
      placeholderTranslation: ETranslation.COMMON_STREET_ADDRESS,
      value: "",
    },
    [EOfferRequest.zip]: {
      type: EInputType.text,
      labelTranslation: ETranslation.COMMON_ZIP,
      placeholderTranslation: ETranslation.COMMON_ZIP,
      value: "",
    },
    [EOfferRequest.city]: {
      type: EInputType.text,
      labelTranslation: ETranslation.COMMON_CITY,
      placeholderTranslation: ETranslation.COMMON_CITY,
      value: "",
    },
    [EOfferRequest.phone]: {
      type: EInputType.text,
      labelTranslation: ETranslation.COMMON_PHONE,
      placeholderTranslation: ETranslation.COMMON_PHONE,
      value: "",
      validation: {
        required: true,
      },
    },
    [EOfferRequest.email]: {
      type: EInputType.email,
      labelTranslation: ETranslation.COMMON_EMAIL,
      placeholderTranslation: ETranslation.COMMON_EMAIL,
      value: "",
      validation: {
        required: true,
      },
    },
    [EOfferRequest.service]: {
      type: EInputType.reactSelect,
      labelTranslation: ETranslation.COMMON_SERVICE,
      placeholderTranslation: ETranslation.COMMON_SERVICE,
      value: "",
      options: [],
      validation: {
        required: true,
      },
    },
    [EOfferRequest.archived]: {
      type: EInputType.checkbox,
      labelTranslation: ETranslation.COMMON_ARCHIVED,
      placeholderTranslation: ETranslation.COMMON_ARCHIVED,
      value: "",
    },
    [EOfferRequest.area]: {
      type: EInputType.text,
      labelTranslation: ETranslation.OFFER_REQUEST_CONSTRUCTION_AREA,
      placeholderTranslation: ETranslation.OFFER_REQUEST_CONSTRUCTION_AREA,
      value: "",
    },
    [EOfferRequest.buildingType]: {
      type: EInputType.reactSelect,
      labelTranslation: ETranslation.OFFER_REQUEST_CONSTRUCTION_BUILDING_TYPE,
      placeholderTranslation: ETranslation.OFFER_REQUEST_CONSTRUCTION_BUILDING_TYPE,
      options: buildingTypes,
      value: EBuildingType.TOWN_HOUSE,
    },
    [EOfferRequest.buildingTypeOther]: {
      type: EInputType.text,
      labelTranslation: ETranslation.OFFER_REQUEST_CONSTRUCTION_BUILDING_TYPE_OTHER,
      placeholderTranslation: ETranslation.OFFER_REQUEST_CONSTRUCTION_BUILDING_TYPE_OTHER,
      value: "",
    },
    [EOfferRequest.constructionYear]: {
      type: EInputType.text,
      labelTranslation: ETranslation.OFFER_REQUEST_CONSTRUCTION_CONSTRUCTION_YEAR,
      placeholderTranslation: ETranslation.OFFER_REQUEST_CONSTRUCTION_CONSTRUCTION_YEAR,
      value: "",
    },
    [EOfferRequest.additionalInfo]: {
      type: EInputType.textarea,
      labelTranslation: ETranslation.OFFERREQUEST_INFO,
      placeholderTranslation: ETranslation.OFFERREQUEST_INFO,
      value: "",
    },
    [EOfferRequest.province]: {
      type: EInputType.text,
      labelTranslation: ETranslation.OFFER_REQUEST_PROVINCE,
      placeholderTranslation: ETranslation.OFFER_REQUEST_PROVINCE,
      value: "",
    },
    [EOfferRequest.municipality]: {
      type: EInputType.text,
      labelTranslation: ETranslation.OFFER_REQUEST_MUNICIPALITY,
      placeholderTranslation: ETranslation.OFFER_REQUEST_MUNICIPALITY,
      value: "",
    },
    [EOfferRequest.language]: {
      type: EInputType.reactSelect,
      labelTranslation: ETranslation.OFFER_REQUEST_LANGUAGE,
      placeholderTranslation: ETranslation.OFFER_REQUEST_LANGUAGE,
      value: "",
      options: []
    },
    [EOfferRequest.length]: {
      type: EInputType.reactSelect,
      labelTranslation: ETranslation.OFFER_REQUEST_WELLNESS_LENGTH,
      placeholderTranslation: ETranslation.OFFER_REQUEST_WELLNESS_LENGTH,
      value: "",
      options: []
    },
    [EOfferRequest.regular]: {
      type: EInputType.checkbox,
      labelTranslation: ETranslation.OFFER_REQUEST_WELLNESS_REGULAR,
      placeholderTranslation: ETranslation.OFFER_REQUEST_WELLNESS_REGULAR,
      value: "",
    },
  });


  let operatorId;
  if (authUser?.isOperator) {
    operatorId = authUser.organization.id;
  } else {
    operatorId = inputs[EOfferRequest.operator].value as string;
  }

  const { serviceOptions, servicesLoading } = useServiceOptions({
    withPlaceholder: true,
    filterByOperatorId: true,
    operatorId
  });

  useEffect(() => {
    if (saveOrUpdateOk) {
      history.push(Routes.OFFER_REQUESTS);
    }
    return () => {
      dispatch(actions.clearOfferRequest());
    };
  }, [dispatch, history, saveOrUpdateOk]);

  const [auditTrails, setAuditTrails] = useState<any[]>([]);
  const [auditTrailsLoading, setAuditTrailsLoading] = useState<boolean>(false);

  useEffect(() => {
    if (Routes.isNotNew(id)) {
      dispatch(actions.getOfferRequest(id));
      if (authUser?.isPrimary || authUser?.isOperator) {
        setAuditTrailsLoading(true);
        axios.get(`/json/offerrequests/audittrail?id=${id}`)
          .then((res) => {
            if (res.data) {
              const list = Array.isArray(res.data) ? res.data : (res.data.auditTrails || []);
              setAuditTrails(list);
            }
          })
          .catch((err) => console.error("Error fetching audit trail:", err))
          .finally(() => setAuditTrailsLoading(false));
      }
    }
  }, [id, dispatch, authUser?.isPrimary, authUser?.isOperator]);

  const dynamicFieldsDef = React.useMemo(() => getDynamicFieldsForService(service?.serviceToken, service?.type), [service]);

  useEffect(() => {
    if (offerRequest) {
      const dataToInit = { ...offerRequest };
      const originalCreated = dataToInit.created;
      if (offerRequest.dynamicFields) {
        Object.assign(dataToInit, offerRequest.dynamicFields);
      }
      if (originalCreated) {
        dataToInit.created = originalCreated;
      }
      if (dataToInit.buildingType) {
        dataToInit.buildingType = mapBuildingTypeToEnum(dataToInit.buildingType);
      }
      setInputs((prev) => {
        const newInputs = { ...prev };
        dynamicFieldsDef.forEach(df => {
          if (!newInputs[df.name as any]) {
            newInputs[df.name as any] = {
              type: df.type,
              label: df.label,
              labelTranslation: df.labelTranslation,
              value: "",
              options: df.options
            }
          } else {
            newInputs[df.name as any].label = df.label;
            newInputs[df.name as any].type = df.type;
            newInputs[df.name as any].labelTranslation = df.labelTranslation;
            newInputs[df.name as any].options = df.options;
          }
        });

        if (dataToInit.dynamicFields) {
          for (const dynKey of Object.keys(dataToInit.dynamicFields)) {
            if (!newInputs[dynKey as any]) {
              newInputs[dynKey as any] = {
                type: EInputType.text,
                label: dynKey,
                value: ""
              }
            }
          }
        }
        return newInputs;
      });
      initForm(setInputs, dataToInit);
    }
  }, [offerRequest, dynamicFieldsDef]);

  const serviceId = inputs[EOfferRequest.service].value as string;

  useEffect(() => {
    if (serviceId && services) {
      setService(services.find((service) => service.id === serviceId) || null);
    } else {
      setService(null);
    }
  }, [serviceId, services]);

  const { createInput } = useInputs({
    inputs,
    setInputs,
    options: {
      updateAction: EInputUpdateAction.OFFERREQUEST,
      disabled: authUser?.isBasic,
    }
  });

  const submitHandler = async (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    event.preventDefault();

    const offerRequestData = getInputData<IOfferRequest>(inputs) as any;

    const knownKeys = ["id", "firstName", "lastName", "address", "phone", "zip", "city", "service", "status", "countryCode", "link", "archived", "operator", "deleted", "additionalInfo", "activeCampaignId", "province", "municipality", "language"];

    const dynamicFields: any = {};
    for (const key of Object.keys(offerRequestData)) {
      if (!knownKeys.includes(key) && offerRequestData[key] !== undefined && offerRequestData[key] !== "") {
        dynamicFields[key] = offerRequestData[key];
        delete offerRequestData[key];
      }
    }

    if (Object.keys(dynamicFields).length > 0) {
      offerRequestData.dynamicFields = dynamicFields;
    }

    if (Routes.isNew(id)) {
      dispatch(actions.saveOfferRequest(offerRequestData));
    } else {
      offerRequestData.id = id;
      dispatch(actions.updateOfferRequest(offerRequestData));
    }
  };

  const deleteOfferRequest = () =>
    setModal({
      isOpen: true,
      title: t(ETranslation.COMMON_REMOVE),
      size: EModalSize.SMALL,
      content: (
        <ConfirmDialog
          body={<p>{t(ETranslation.CONFIRM_DELETE_OFFER_REQUEST)}</p>}
          onConfirm={() => {
            dispatch(actions.deleteOfferRequest(id));
            history.goBack();
            setModal(defaultModal);
          }}
          onCancel={() => setModal(defaultModal)}
        />
      ),
    });

  const writeContactToClipboardHandler = () => {
    const dynFieldsLabels = Object.keys(offerRequest?.dynamicFields || {})
      .filter(key => key !== 'acceptTerms')
      .map(key => {
        const fieldRef = dynamicFieldsDef.find(d => d.name === key);
        const val = (offerRequest?.dynamicFields as any)[key];
        const labelStr = fieldRef?.labelTranslation ? t(fieldRef.labelTranslation) : (fieldRef?.label || key);
        let valStr = (val === "on" || val === true || val === "true") ? t(ETranslation.COMMON_YES) : val;

        if (fieldRef?.options) {
          const option = fieldRef.options.find((o: any) => o.value === val);
          if (option) {
            valStr = option.labelTranslation ? t(option.labelTranslation) : (option.label || val);
          }
        }

        return `${labelStr}: ${valStr}`;
      });

    const info = [
      offerRequest?.firstName,
      offerRequest?.lastName, '\n',
      offerRequest?.address, '\n',
      offerRequest?.zip,
      offerRequest?.city, '\n',
      offerRequest?.province ? `${t(ETranslation.OFFER_REQUEST_PROVINCE)}: ${offerRequest.province}\n` : null,
      offerRequest?.municipality ? `${t(ETranslation.OFFER_REQUEST_MUNICIPALITY)}: ${offerRequest.municipality}\n` : null,
      offerRequest?.buildingType ? `${t(ETranslation.OFFER_REQUEST_CONSTRUCTION_BUILDING_TYPE)}: ${
        (() => {
          const mappedType = mapBuildingTypeToEnum(offerRequest.buildingType as string);
          const bt = buildingTypes.find(b => b.value === mappedType);
          return bt?.labelTranslation ? t(bt.labelTranslation) : offerRequest.buildingType;
        })()
      }\n` : null,
      offerRequest?.area ? `${t(ETranslation.OFFER_REQUEST_CONSTRUCTION_AREA)}: ${offerRequest.area}\n` : null,
      offerRequest?.constructionYear ? `${t(ETranslation.OFFER_REQUEST_CONSTRUCTION_CONSTRUCTION_YEAR)}: ${offerRequest.constructionYear}\n` : null,
      offerRequest?.phone, '\n',
      offerRequest?.email,
      ...dynFieldsLabels.length > 0 ? ['\n', '\n', 'Lisätiedot:', '\n', ...dynFieldsLabels.map(d => d + '\n')] : [],
      ...offerRequest?.additionalInfo ? ['\n', '\n', t(ETranslation.OFFERREQUEST_INFO), '\n'] : [],
      ...offerRequest?.additionalInfo ? [offerRequest?.additionalInfo] : []
    ];
    const notNullInfo = info.filter(function (e) { return e != null && e !== ''; });
    console.log(notNullInfo.join(' ') + " Copied to clipboard");
    return (notNullInfo.join(' '));
  };

  return (
    <>
      {error && error !== "No offerRequestAccess with given id" && <Alert children={error} />}
      <Container>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '2rem', flexWrap: 'wrap', gap: '1rem' }}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <FontAwesomeIcon icon={faArrowLeft} style={{ cursor: 'pointer', marginRight: '1rem', fontSize: '1.2rem', color: '#334155' }} onClick={() => history.goBack()} />
            <h2 style={{ margin: 0, color: '#0f172a' }}>
              {Routes.isNew(id)
                ? t(ETranslation.TITLE_OFFERREQUEST_NEW)
                : (isEditing ? "Tarjouspyynnön muokkaus" : "Tarjouspyynnön tiedot")}
            </h2>
            {service && (
              <span style={{ marginLeft: '1rem', padding: '4px 12px', backgroundColor: '#f1f5f9', borderRadius: '16px', fontSize: '0.85rem', color: '#475569', fontWeight: 600 }}>
                {service.name}
              </span>
            )}
          </div>

          {(authUser?.isPrimary || authUser?.isOperator) && Routes.isNotNew(id) && !offerRequest?.deleted && (
            <div>
              {isEditing ? (
                <Button
                  onClick={() => setIsEditing(false)}
                  style={{ backgroundColor: '#64748b', color: 'white', display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '8px 16px', borderRadius: '6px' }}
                >
                  <FontAwesomeIcon icon={faTimes} />
                  <span>Sulje muokkaus</span>
                </Button>
              ) : (
                <Button
                  onClick={() => setIsEditing(true)}
                  style={{ backgroundColor: '#2563eb', color: 'white', display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '8px 16px', borderRadius: '6px' }}
                >
                  <FontAwesomeIcon icon={faEdit} />
                  <span>Muokkaustila</span>
                </Button>
              )}
            </div>
          )}
        </div>

        {error === "No offerRequestAccess with given id" ? (
          <div style={{
            backgroundColor: '#fffbeb',
            border: '1px solid #fef3c7',
            borderRadius: '12px',
            padding: '3rem 2rem',
            textAlign: 'center',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -2px rgba(0, 0, 0, 0.05)',
            maxWidth: '600px',
            margin: '2rem auto',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '1rem'
          }}>
            <div style={{
              width: '64px',
              height: '64px',
              borderRadius: '50%',
              backgroundColor: '#fef3c7',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#d97706',
              fontSize: '1.75rem',
              marginBottom: '0.5rem'
            }}>
              <FontAwesomeIcon icon={faLock} />
            </div>
            <h3 style={{ margin: 0, color: '#92400e', fontSize: '1.4rem', fontWeight: 600 }}>Tarjouspyyntö on lukittu</h3>
            <p style={{ margin: 0, color: '#78350f', lineHeight: '1.6', fontSize: '1.05rem', maxWidth: '480px' }}>
              Sinulla ei ole vielä katseluoikeutta tähän tarjouspyyntöön. Voit lunastaa asiakkaan yhteystiedot ja avata lukituksen palaamalla myyntiputkeen tai tarjouspyyntölistaukseen ja klikkaamalla <strong>'Osta'</strong>.
            </p>
            <div style={{ marginTop: '1.5rem', display: 'flex', gap: '1rem' }}>
              <button 
                onClick={() => history.push(Routes.OFFER_REQUESTS)}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#d97706',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  fontWeight: 600,
                  fontSize: '0.95rem',
                  cursor: 'pointer',
                  transition: 'background-color 0.2s',
                  boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)'
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#b45309'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#d97706'}
              >
                Siirry tarjouspyyntöihin
              </button>
              <button 
                onClick={() => history.goBack()}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#f1f5f9',
                  color: '#475569',
                  border: '1px solid #e2e8f0',
                  borderRadius: '6px',
                  fontWeight: 600,
                  fontSize: '0.95rem',
                  cursor: 'pointer',
                  transition: 'background-color 0.2s, color 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#e2e8f0';
                  e.currentTarget.style.color = '#1e293b';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#f1f5f9';
                  e.currentTarget.style.color = '#475569';
                }}
              >
                Palaa takaisin
              </button>
            </div>
          </div>
        ) : (
          <>
            <div style={{ display: 'flex', gap: '2rem', flexWrap: 'wrap', alignItems: 'flex-start' }}>
              <div style={{ flex: '1 1 60%', minWidth: '300px' }}>
                {loading ? (
                  <Spinner />
                ) : error && !offerRequest ? (
                  <p style={{ color: '#ef4444', fontWeight: 500 }}>{error}</p>
                ) : offerRequest?.deleted ? (
                  <p>{t(ETranslation.OFFER_REQUEST_DELETED)}</p>
                ) : offerRequest?.archived && authUser?.isServiceProvider && !offerRequest?.hasWonOffer && offerRequest?.offerStatus !== 'WON' ? (
                  <p>{t(ETranslation.OFFER_REQUEST_ARCHIVED)}</p>
                ) : !isEditing ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                    {offerRequest?.archived && (
                      <Alert color={EAlertColor.PRIMARY} noHorizontalMargin>
                        Asiakas on sulkenut tarjouspyynnön uusilta tarjouksilta. Olet voittanut tämän tarjouksen, joten asiakkaan yhteystiedot ovat edelleen käytettävissäsi palvelun suorittamista varten.
                      </Alert>
                    )}
                    <div style={{ backgroundColor: 'white', borderRadius: '8px', border: '1px solid #e2e8f0', padding: '1.5rem' }}>
                      <h3 style={{ marginTop: 0, marginBottom: '1.5rem', fontSize: '1.1rem', color: '#0f172a' }}>Asiakkaan tiedot</h3>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                        {/* RIVI 1: Yleistiedot (Päivämäärä, Kieli, Operaattori) */}
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1.5rem', borderBottom: '1px solid #f1f5f9', paddingBottom: '1.25rem' }}>
                          <div>
                            <div style={{ fontSize: '0.8rem', color: '#64748b', marginBottom: '4px' }}>Päivämäärä</div>
                            {(() => {
                              const timeInfo = formatRelativeTime(offerRequest?.created);
                              if (!timeInfo) return <div style={{ fontWeight: 500, color: '#334155' }}>-</div>;
                              return (
                                <div>
                                  <div style={{ fontWeight: 500, color: '#334155' }}>{timeInfo.formatted}</div>
                                  <div style={{ fontSize: '0.75rem', color: '#64748b', marginTop: '2px', fontWeight: 500 }}>{timeInfo.relative}</div>
                                </div>
                              );
                            })()}
                          </div>
                          <div>
                            <div style={{ fontSize: '0.8rem', color: '#64748b', marginBottom: '4px' }}>Kieli</div>
                            <div style={{ fontWeight: 500, color: '#334155' }}>{offerRequest?.language || 'suomi'}</div>
                          </div>
                          {offerRequest?.operator?.name ? (
                            <div>
                              <div style={{ fontSize: '0.8rem', color: '#64748b', marginBottom: '4px' }}>Operaattori</div>
                              <div style={{ fontWeight: 500, color: '#334155' }}>{offerRequest.operator.name}</div>
                            </div>
                          ) : <div></div>}
                        </div>

                    {/* RIVI 2: Yhteystiedot (Nimi, Puhelinnumero, Sähköposti) */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1.5rem', borderBottom: '1px solid #f1f5f9', paddingBottom: '1.25rem' }}>
                      <div>
                        <div style={{ fontSize: '0.8rem', color: '#64748b', marginBottom: '4px' }}>Nimi</div>
                        <div style={{ fontWeight: 500, color: '#334155' }}>{[offerRequest?.firstName, offerRequest?.lastName].filter(Boolean).join(' ') || '-'}</div>
                      </div>
                      <div>
                        <div style={{ fontSize: '0.8rem', color: '#64748b', marginBottom: '4px' }}>Puhelinnumero</div>
                        <div style={{ fontWeight: 500, color: '#334155' }}>{offerRequest?.phone || '-'}</div>
                      </div>
                      <div>
                        <div style={{ fontSize: '0.8rem', color: '#64748b', marginBottom: '4px' }}>Sähköposti</div>
                        <div style={{ fontWeight: 500, color: '#334155', wordBreak: 'break-all' }}>{offerRequest?.email || '-'}</div>
                      </div>
                    </div>

                    {/* RIVI 3: Kohteen osoite */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1.5rem', borderBottom: '1px solid #f1f5f9', paddingBottom: '1.25rem' }}>
                      <div style={{ gridColumn: 'span 3' }}>
                        <div style={{ fontSize: '0.8rem', color: '#64748b', marginBottom: '4px' }}>Osoite</div>
                        {(() => {
                          const addressParts = [
                            offerRequest?.address,
                            [offerRequest?.zip, offerRequest?.city].filter(Boolean).join(' ')
                          ].filter(Boolean);
                          const fullAddress = addressParts.join(', ');
                          if (!fullAddress) return <div style={{ fontWeight: 500, color: '#334155' }}>-</div>;
                          const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(fullAddress)}`;
                          return (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                              <span style={{ fontWeight: 500, color: '#334155' }}>{fullAddress}</span>
                              <a 
                                href={mapsUrl} 
                                target="_blank" 
                                rel="noopener noreferrer" 
                                style={{ 
                                  display: 'inline-flex', 
                                  alignItems: 'center', 
                                  gap: '6px', 
                                  color: '#3b82f6', 
                                  textDecoration: 'none', 
                                  fontSize: '0.85rem',
                                  fontWeight: 500,
                                  transition: 'color 0.2s'
                                }}
                                onMouseEnter={(e) => e.currentTarget.style.color = '#1d4ed8'}
                                onMouseLeave={(e) => e.currentTarget.style.color = '#3b82f6'}
                              >
                                <FontAwesomeIcon icon={faMapMarkerAlt} style={{ fontSize: '0.95rem' }} />
                                <span>Avaa Google Maps</span>
                                <FontAwesomeIcon icon={faExternalLinkAlt} style={{ fontSize: '0.75rem', opacity: 0.7 }} />
                              </a>
                            </div>
                          );
                        })()}
                      </div>
                    </div>

                    {/* RIVI 4: Dynaamiset kentät (Rakennustyyppi, Pinta-ala, Rakennusvuosi ja muut) */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1.5rem' }}>
                        {offerRequest?.buildingType && (
                          <div>
                            <div style={{ fontSize: '0.8rem', color: '#64748b', marginBottom: '4px' }}>Rakennustyyppi</div>
                            <div style={{ fontWeight: 500, color: '#334155' }}>
                              {(() => {
                                const mappedType = mapBuildingTypeToEnum(offerRequest.buildingType as string);
                                const bt = buildingTypes.find(b => b.value === mappedType);
                                return bt?.labelTranslation ? t(bt.labelTranslation) : offerRequest?.buildingType;
                              })()}
                            </div>
                          </div>
                        )}
                        {offerRequest?.area && (
                          <div>
                            <div style={{ fontSize: '0.8rem', color: '#64748b', marginBottom: '4px' }}>Pinta-ala (m²)</div>
                            <div style={{ fontWeight: 500, color: '#334155' }}>{offerRequest?.area}</div>
                          </div>
                        )}
                        {offerRequest?.constructionYear && (
                          <div>
                            <div style={{ fontSize: '0.8rem', color: '#64748b', marginBottom: '4px' }}>Rakennusvuosi</div>
                            <div style={{ fontWeight: 500, color: '#334155' }}>{offerRequest?.constructionYear}</div>
                          </div>
                        )}
                        
                        {/* Dynamic fields mapping */}
                        {dynamicFieldsDef
                          .filter(df => df.name !== 'acceptTerms')
                          .filter(df => !df.condition || df.condition(inputs))
                          .map(df => {
                            const val = offerRequest?.dynamicFields?.[df.name];
                            if (!val) return null;
                            let displayVal = (val === "on" || val === true || val === "true") ? "Kyllä" : val;
                            
                            if (df.name === 'buildingType') {
                              const mappedType = mapBuildingTypeToEnum(val as string);
                              let found = false;
                              
                              if (df.options) {
                                const option = df.options.find((o: any) => o.value === mappedType || o.value === val);
                                if (option) {
                                  displayVal = option.labelTranslation ? t(option.labelTranslation) : (option.label || val);
                                  found = true;
                                }
                              }
                              
                              if (!found) {
                                const bt = buildingTypes.find(b => b.value === mappedType);
                                if (bt?.labelTranslation) displayVal = t(bt.labelTranslation);
                                else if (bt?.label) displayVal = bt.label;
                              }
                            } else if (df.options) {
                              const option = df.options.find((o: any) => o.value === val);
                              if (option) {
                                displayVal = option.labelTranslation ? t(option.labelTranslation) : (option.label || val);
                              }
                            }
                            
                            return (
                              <div key={df.name}>
                                <div style={{ fontSize: '0.8rem', color: '#64748b', marginBottom: '4px' }}>{df.labelTranslation ? t(df.labelTranslation) : df.label}</div>
                                <div style={{ fontWeight: 500, color: '#334155' }}>{displayVal}</div>
                              </div>
                            );
                          })}
                      </div>
                    </div>
                    
                    {offerRequest?.additionalInfo && (
                      <div style={{ marginTop: '1.5rem', borderTop: '1px solid #f1f5f9', paddingTop: '1.5rem' }}>
                        <div style={{ fontSize: '0.85rem', fontWeight: 600, color: '#475569', marginBottom: '0.5rem' }}>Lisätiedot ja tarkennukset</div>
                        <p style={{ margin: 0, color: '#334155', lineHeight: '1.6', whiteSpace: 'pre-line' }}>{offerRequest.additionalInfo}</p>
                      </div>
                    )}

                    <AttachmentsList attachments={offerRequest?.attachments} offerRequestId={offerRequest?.id} customerEmail={offerRequest?.email} />
                  </div>

                  <div style={{ display: 'flex', gap: '1rem' }}>
                    <Button onClick={() => { navigator.clipboard.writeText(writeContactToClipboardHandler()); alert(t(ETranslation.COMMON_COPIED)); }}>
                      {t(ETranslation.COMMON_COPY_CONTACT_INFORMATION)}
                    </Button>
                  </div>

                  {Routes.isNotNew(id) && authUser?.isServiceProvider && (
                    <OfferEdit offerRequestId={id} offerRequest={offerRequest || undefined} />
                  )}
                </div>
              ) : (
                /* EDIT MODE WITH INPUTS */
                <div style={{ backgroundColor: 'white', borderRadius: '8px', border: '1px solid #e2e8f0', padding: '1.5rem' }}>
                  <h3 style={{ marginTop: 0, marginBottom: '1.5rem', fontSize: '1.1rem', color: '#0f172a' }}>
                    Muokkaa tarjouspyynnön tietoja
                  </h3>

                  {authUser?.isPrimary &&
                    createInput(EOfferRequest.operator, {
                      options: organizationOptions,
                      loading: organizationsLoading,
                    })}
                  <InputGroup>
                    {createInput(EOfferRequest.firstName)}
                    {createInput(EOfferRequest.lastName)}
                  </InputGroup>
                  {service?.type !== EOfferRequestType.WELLNESS ? (
                    <>
                      <InputGroup>
                        {createInput(EOfferRequest.address)}
                        {createInput(EOfferRequest.zip)}
                        {createInput(EOfferRequest.city)}
                      </InputGroup>
                      <InputGroup>
                        {createInput(EOfferRequest.province)}
                        {createInput(EOfferRequest.municipality)}
                      </InputGroup>
                    </>
                  ) : (
                    <InputGroup>
                      {createInput(EOfferRequest.city)}
                      {createInput(EOfferRequest.province)}
                      {createInput(EOfferRequest.municipality)}
                    </InputGroup>
                  )}
                  <InputGroup>
                    {createInput(EOfferRequest.phone)}
                    {createInput(EOfferRequest.email)}
                  </InputGroup>
                  <InputGroup>
                    {createInput(EOfferRequest.service, {
                      options: serviceOptions,
                      loading: servicesLoading,
                    })}
                    {createInput(EOfferRequest.language, {
                      options: languages
                    })}
                  </InputGroup>
                  {dynamicFieldsDef.length > 0 && (
                    <>
                      {dynamicFieldsDef
                        .filter((df) => df.name !== 'acceptTerms')
                        .filter((df) => !df.condition || df.condition(inputs))
                        .filter((df) => ![EOfferRequest.city, EOfferRequest.additionalInfo].includes(df.name as any))
                        .reduce((acc: any[][], curr, i) => {
                          if (i % 2 === 0) acc.push([curr]);
                          else acc[acc.length - 1].push(curr);
                          return acc;
                        }, [])
                        .map((chunk, chunkIndex) => (
                          <InputGroup key={`dynamic-chunk-${chunkIndex}`}>
                            {chunk.map((df) => (
                              <React.Fragment key={df.name}>
                                {createInput(df.name as any)}
                              </React.Fragment>
                            ))}
                          </InputGroup>
                        ))}
                    </>
                  )}

                  {/* Catch-all for unmapped dynamic Fields saved on the item */}
                  {offerRequest?.dynamicFields && (
                    <>
                      {Object.keys(offerRequest.dynamicFields)
                        .filter(key =>
                          !dynamicFieldsDef.find(d => d.name === key) &&
                          !["firstName", "lastName", "email", "phone", "address", "zip", "city", "province", "municipality", "language", "additionalInfo", "operator", "service", "activeCampaignId", "archived", "id", "deleted", "status", "countryCode", "link", "formToken", "latitude", "longitude", "buildingType", "area", "constructionYear"].includes(key)
                        )
                        .reduce((acc: any[][], curr, i) => {
                          if (i % 2 === 0) acc.push([curr]);
                          else acc[acc.length - 1].push(curr);
                          return acc;
                        }, [])
                        .map((chunk, chunkIndex) => (
                          <InputGroup key={`legacy-chunk-${chunkIndex}`}>
                            {chunk.map((key) => (
                              <React.Fragment key={`legacy-dyn-${key}`}>
                                {createInput(key as any, { disabled: true })}
                              </React.Fragment>
                            ))}
                          </InputGroup>
                        ))}
                    </>
                  )}
                  {createInput(EOfferRequest.additionalInfo)}
                  {createInput(EOfferRequest.archived)}
                  <AttachmentsList attachments={offerRequest?.attachments} offerRequestId={offerRequest?.id} customerEmail={offerRequest?.email} />

                  <div style={{ marginTop: '1.5rem', paddingTop: '1.5rem', borderTop: '1px solid #e2e8f0' }}>
                    <InputGroup>
                      {!authUser?.isBasic && !offerRequest?.deleted && (
                        <Button disabled={loading} onClick={submitHandler}>
                          {t(ETranslation.COMMON_SAVE)}
                        </Button>
                      )}
                      <Button onClick={() => Routes.isNew(id) ? history.goBack() : setIsEditing(false)}>
                        {t(ETranslation.COMMON_RETURN)}
                      </Button>
                      {(authUser?.isPrimary || authUser?.isOperator) &&
                        Routes.isNotNew(id) &&
                        !offerRequest?.deleted && (
                          <Button disabled={loading} onClick={deleteOfferRequest}>
                            {t(ETranslation.COMMON_REMOVE)}
                          </Button>
                        )}
                      <Button onClick={() => { navigator.clipboard.writeText(writeContactToClipboardHandler()); alert(t(ETranslation.COMMON_COPIED)); }}>
                        {t(ETranslation.COMMON_COPY_CONTACT_INFORMATION)}
                      </Button>
                    </InputGroup>
                  </div>
                </div>
              )}
            </div>
            
            <div style={{ flex: '1', minWidth: '300px' }}>
              {authUser?.isServiceProvider ? (
                <OfferRequestsInfo authUser={authUser as any} />
              ) : (
                <>
                  {Routes.isNotNew(id) && !loading && (
                    <div style={{ border: '1px solid #ddd', padding: '1.5rem', borderRadius: '4px', backgroundColor: '#f9f9f9' }}>
                      <h3 style={{ marginTop: 0, marginBottom: '1rem', borderBottom: '1px solid #ccc', paddingBottom: '0.5rem' }}>
                        Tekniset tiedot (RequestInfo)
                      </h3>
                      {offerRequest?.requestInfo?.properties && Object.keys(offerRequest.requestInfo.properties).length > 0 ? (
                        <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                          {Object.entries(offerRequest.requestInfo.properties).map(([key, val]: any) => {
                            const displayVal = val.stringValue || (val.nullValue === null ? 'null' : (val.booleanValue !== undefined ? val.booleanValue.toString() : JSON.stringify(val)));
                            return (
                              <li key={key} style={{ marginBottom: '0.75rem', wordBreak: 'break-word', fontSize: '0.9rem' }}>
                                <strong style={{ display: 'block', color: '#555' }}>{key}:</strong>
                                <span style={{ color: displayVal === 'null' ? '#aaa' : '#000' }}>{displayVal}</span>
                              </li>
                            );
                          })}
                        </ul>
                      ) : (
                        <p style={{ color: '#888', fontStyle: 'italic' }}>tiedot puuttuu</p>
                      )}
                    </div>
                  )}

                  {Routes.isNotNew(id) && !loading && (
                    <div style={{ border: '1px solid #ddd', padding: '1.5rem', borderRadius: '4px', backgroundColor: '#f9f9f9', marginTop: '1.5rem' }}>
                      <h3 style={{ marginTop: 0, marginBottom: '1rem', borderBottom: '1px solid #ccc', paddingBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span>🧠 Tulkitut tiedot (AI / Geo)</span>
                      </h3>
                      <ul style={{ listStyle: 'none', padding: 0, margin: 0, fontSize: '0.9rem' }}>
                        <li style={{ marginBottom: '0.85rem' }}>
                          <strong style={{ display: 'block', color: '#555', marginBottom: '4px' }}>Asiakastyyppi (AI):</strong>
                          {offerRequest?.customerType ? (
                            <span style={{
                              display: 'inline-block',
                              padding: '2px 8px',
                              borderRadius: '4px',
                              fontWeight: 600,
                              fontSize: '0.85rem',
                              backgroundColor: offerRequest.customerType === 'CONSUMER' ? '#e0f2fe' : offerRequest.customerType === 'BUSINESS' ? '#fef3c7' : offerRequest.customerType === 'PUBLIC_SECTOR' ? '#f3e8ff' : '#f1f5f9',
                              color: offerRequest.customerType === 'CONSUMER' ? '#0369a1' : offerRequest.customerType === 'BUSINESS' ? '#92400e' : offerRequest.customerType === 'PUBLIC_SECTOR' ? '#6b21a8' : '#334155',
                            }}>
                              {offerRequest.customerType === 'CONSUMER' ? '👤 Kuluttaja (CONSUMER)' :
                               offerRequest.customerType === 'BUSINESS' ? '🏢 Yritys / Taloyhtiö (BUSINESS)' :
                               offerRequest.customerType === 'PUBLIC_SECTOR' ? '🏛️ Julkinen sektori (PUBLIC_SECTOR)' :
                               offerRequest.customerType === 'NON_PROFIT' ? '🤝 Yhdistys / Järjestö (NON_PROFIT)' :
                               offerRequest.customerType}
                            </span>
                          ) : (
                            <span style={{ color: '#aaa', fontStyle: 'italic' }}>ei määritelty</span>
                          )}
                        </li>

                        <li style={{ marginBottom: '0.85rem' }}>
                          <strong style={{ display: 'block', color: '#555', marginBottom: '2px' }}>Maakunta (Geo):</strong>
                          <span style={{ color: offerRequest?.province ? '#1e293b' : '#aaa', fontStyle: offerRequest?.province ? 'normal' : 'italic', fontWeight: offerRequest?.province ? 500 : 400 }}>
                            {offerRequest?.province || 'ei määritelty'}
                          </span>
                        </li>

                        <li style={{ marginBottom: '0.85rem' }}>
                          <strong style={{ display: 'block', color: '#555', marginBottom: '2px' }}>Kunta (Geo):</strong>
                          <span style={{ color: offerRequest?.municipality ? '#1e293b' : '#aaa', fontStyle: offerRequest?.municipality ? 'normal' : 'italic', fontWeight: offerRequest?.municipality ? 500 : 400 }}>
                            {offerRequest?.municipality || 'ei määritelty'}
                          </span>
                        </li>

                        <li style={{ marginBottom: '0.25rem' }}>
                          <strong style={{ display: 'block', color: '#555', marginBottom: '4px' }}>Geokoordinaatit (Lat / Lng):</strong>
                          {offerRequest?.latitude != null && offerRequest?.longitude != null ? (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                              <span style={{ fontFamily: 'monospace', backgroundColor: '#e2e8f0', padding: '2px 6px', borderRadius: '4px', fontSize: '0.85rem', color: '#1e293b' }}>
                                {offerRequest.latitude.toFixed(6)}, {offerRequest.longitude.toFixed(6)}
                              </span>
                              <a
                                href={`https://www.google.com/maps/search/?api=1&query=${offerRequest.latitude},${offerRequest.longitude}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: '5px',
                                  color: '#2563eb',
                                  textDecoration: 'none',
                                  fontSize: '0.85rem',
                                  fontWeight: 600
                                }}
                                title="Avaa kohteen sijainti Google Mapsissa uuteen välilehteen"
                              >
                                <FontAwesomeIcon icon={faMapMarkerAlt} style={{ color: '#dc2626' }} />
                                <span>Google Maps</span>
                                <FontAwesomeIcon icon={faExternalLinkAlt} style={{ fontSize: '0.75rem', opacity: 0.7 }} />
                              </a>
                            </div>
                          ) : (
                            <span style={{ color: '#aaa', fontStyle: 'italic' }}>ei koordinaatteja</span>
                          )}
                        </li>
                      </ul>
                    </div>
                  )}

                  {(authUser?.isPrimary || authUser?.isOperator) && Routes.isNotNew(id) && !loading && offerRequest?.link && (
                    <div style={{ border: '1px solid #ddd', padding: '1.5rem', borderRadius: '4px', backgroundColor: '#f9f9f9', marginTop: '1.5rem' }}>
                      <h3 style={{ marginTop: 0, marginBottom: '1rem', borderBottom: '1px solid #ccc', paddingBottom: '0.5rem' }}>
                        Asiakkaan näkymä
                      </h3>
                      <p style={{ marginBottom: '0.5rem', fontSize: '0.9rem', color: '#555' }}>Voit kopioida asiakkaan henkilökohtaisen tarjouspyyntölinkin tästä:</p>
                      <div style={{ display: 'flex', gap: '0.5rem' }}>
                        <input type="text" value={offerRequest.link} disabled style={{ flex: '1', padding: '0.5rem', border: '1px solid #ccc', borderRadius: '4px', backgroundColor: '#eee', color: '#666' }} />
                        <Button onClick={() => { navigator.clipboard.writeText(offerRequest.link!); alert(t(ETranslation.COMMON_COPIED) || "Kopioitu!"); }}>
                          <FontAwesomeIcon icon={faCopy} />
                        </Button>
                      </div>
                    </div>
                  )}

                  {(authUser?.isPrimary || authUser?.isOperator) &&
                    offerRequest?.activeCampaignId && (
                      <div style={{ marginTop: "1rem" }}>
                        <Button
                          onClick={() =>
                            window.open(
                              "https://positiveproductions.activehosted.com/app/contacts/" +
                              offerRequest?.activeCampaignId,
                              "_blank"
                            )
                          }
                        >
                          ActiveCampaign contact: {offerRequest?.activeCampaignId}
                        </Button>
                      </div>
                    )}
                </>
              )}
            </div>
          </div>
        </>
      )}

        {/* Coverage Metrics & AuditTrail section */}
        {(authUser?.isPrimary || authUser?.isOperator) && Routes.isNotNew(id) && !loading && (
          <div style={{ marginTop: '2rem', borderTop: '2px solid #e2e8f0', paddingTop: '1.5rem', width: '100%' }}>
            <h2 style={{ fontSize: '1.25rem', marginBottom: '1rem', color: '#1e293b' }}>
              📊 Tarjouspyynnön tila ja AuditTrail
            </h2>

            {/* Coverage cards */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
              <div style={{ padding: '1rem', backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '6px' }}>
                <strong style={{ display: 'block', fontSize: '0.85rem', color: '#64748b' }}>Katselukerrat (viewCount)</strong>
                <span style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#0f172a' }}>{offerRequest?.viewCount || 0}</span>
              </div>
              <div style={{ padding: '1rem', backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '6px' }}>
                <strong style={{ display: 'block', fontSize: '0.85rem', color: '#64748b' }}>Aktiiviset tarjoukset (offerCount)</strong>
                <span style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#0f172a' }}>{offerRequest?.offerCount || 0}</span>
              </div>
              <div style={{ padding: '1rem', backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '6px' }}>
                <strong style={{ display: 'block', fontSize: '0.85rem', color: '#64748b' }}>Laskennallinen tila</strong>
                <span style={{ fontSize: '1.1rem', fontWeight: 'bold', color: offerRequest?.matchStatus === 'MATCHED' ? '#16a34a' : offerRequest?.matchStatus === 'SIGNALED_MORE' ? '#d97706' : '#2563eb' }}>
                  {offerRequest?.matchStatus === 'MATCHED' ? '🟢 Käsitelty' : offerRequest?.matchStatus === 'SIGNALED_MORE' ? '⚡ Lisätarjouksia toivottu' : offerRequest?.matchStatus === 'IN_VIEWING' ? '🟡 Katseltu' : '🔴 Uusi'}
                </span>
              </div>
              {offerRequest?.moreOffersRequestedAt && (
                <div style={{ padding: '1rem', backgroundColor: '#fef3c7', border: '1px solid #fde68a', borderRadius: '6px' }}>
                  <strong style={{ display: 'block', fontSize: '0.85rem', color: '#92400e' }}>Lisätarjouksia pyydetty</strong>
                  <span style={{ fontSize: '0.95rem', fontWeight: 'bold', color: '#78350f' }}>{formatDateString(offerRequest.moreOffersRequestedAt)}</span>
                </div>
              )}
            </div>

            {/* AuditTrail Table */}
            <h3 style={{ fontSize: '1.1rem', marginBottom: '0.75rem', color: '#334155' }}>
              📋 Lokitiedot (AuditTrail)
            </h3>
            {auditTrailsLoading ? (
              <Spinner />
            ) : auditTrails && auditTrails.length > 0 ? (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem', textAlign: 'left' }}>
                  <thead>
                    <tr style={{ backgroundColor: '#f1f5f9', borderBottom: '2px solid #cbd5e1' }}>
                      <th style={{ padding: '8px 12px' }}>Aika</th>
                      <th style={{ padding: '8px 12px' }}>Tapahtuma</th>
                      <th style={{ padding: '8px 12px' }}>Käyttäjä / Palveluntarjoaja</th>
                    </tr>
                  </thead>
                  <tbody>
                    {auditTrails.map((trail: any, idx: number) => {
                      const eventStr = trail.event || trail.action || '';
                      let eventLabel = eventStr;
                      if (eventStr === 'VIEW' || eventStr === 'OFFER_REQUEST_VIEW') eventLabel = '👀 Katselu (Palveluntarjoaja)';
                      else if (eventStr === 'VIEW_PIN' || eventStr === 'OFFER_REQUEST_VIEW_PIN') eventLabel = '🔑 Katselu (Asiakas)';
                      else if (eventStr === 'SAVE_NEW' || eventStr === 'OFFER_REQUEST_SAVE') eventLabel = '➕ Luotu uusi';
                      else if (eventStr === 'UPDATE' || eventStr === 'OFFER_REQUEST_UPDATE') eventLabel = '✏️ Muokattu';
                      else if (eventStr === 'SIGNAL_MORE' || eventStr === 'OFFER_REQUEST_SIGNAL_MORE') eventLabel = '⚡ Pyydetty lisää tarjouksia';
                      else if (eventStr === 'ADD_ACCESS') eventLabel = '🔓 Mikromaksu / Oikeus lunastettu';

                      const u = trail.user || trail.actionUser;
                      let actorLabel = '';
                      if (u) {
                        const orgName = u.organization?.name;
                        const fullName = [u.firstName, u.lastName].filter(Boolean).join(' ');
                        if (orgName && fullName) {
                          actorLabel = `${orgName} (${fullName})`;
                        } else if (orgName) {
                          actorLabel = orgName;
                        } else if (fullName) {
                          actorLabel = `${fullName} (${u.email || ''})`;
                        } else {
                          actorLabel = u.email || 'Palveluntarjoaja';
                        }
                      } else if (['VIEW_PIN', 'OFFER_REQUEST_VIEW_PIN', 'SIGNAL_MORE', 'OFFER_REQUEST_SIGNAL_MORE'].includes(eventStr)) {
                        actorLabel = '👤 Asiakas (PIN-linkki)';
                      } else if (trail.userEmail) {
                        actorLabel = trail.userEmail;
                      } else {
                        actorLabel = '👤 Asiakas';
                      }

                      return (
                        <tr key={trail.id || idx} style={{ borderBottom: '1px solid #e2e8f0', backgroundColor: idx % 2 === 0 ? '#fff' : '#f8fafc' }}>
                          <td style={{ padding: '8px 12px', whiteSpace: 'nowrap', fontWeight: 500 }}>{trail.created ? formatDateTimeString(trail.created) : '-'}</td>
                          <td style={{ padding: '8px 12px' }}>{eventLabel}</td>
                          <td style={{ padding: '8px 12px', fontWeight: 600, color: '#1e293b' }}>{actorLabel}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <p style={{ color: '#64748b', fontStyle: 'italic' }}>Ei AuditTrail-lokitietoja saatavilla tästä tarjouspyynnöstä.</p>
            )}
          </div>
        )}
      </Container>
    </>
  );
};

export default OfferRequest;
